import { useRoute } from '#imports'
import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { reviewsConfig } from '../constants'

type UseReviewsQueryParamsOptions = {
  defaultOrderBy?: string
  pageSize?: number
  cursor?: string
}

export function useReviewsQueryParams(
  options?: MaybeRefOrGetter<UseReviewsQueryParamsOptions>,
) {
  const route = useRoute()
  const locale = useI18nLocale()

  return computed(() => {
    const {
      defaultOrderBy = '-relevance',
      pageSize,
      cursor: cursorOverride,
    } = toValue(options) ?? {}

    const routeName = route.name?.toString() ?? ''
    const reviewsListQuery = reviewsConfig?.[routeName]?.reviewsListQuery

    /* eslint-disable camelcase -- review API query params are snake_case */
    const {
      global_rate__gte,
      global_rate__lte,
      backbox_grade_label__in,
      tags__in,
      order_by,
      with_attachments,
      seller_public_id,
      locale: localeParam,
      translation_locale,
      cursor,
    } = route.query ?? {}

    const mergedTagsIn = [
      ...new Set([
        ...(reviewsListQuery?.tags__in
          ? String(reviewsListQuery.tags__in).split(',')
          : []),
        ...(tags__in ? String(tags__in).split(',') : []),
      ]),
    ]

    return removeEmptyValuesInObject({
      global_rate__gte: global_rate__gte ? Number(global_rate__gte) : undefined,
      global_rate__lte: global_rate__lte ? Number(global_rate__lte) : undefined,
      backbox_grade_label__in: backbox_grade_label__in
        ? String(backbox_grade_label__in)
        : undefined,
      tags__in: mergedTagsIn.length > 0 ? mergedTagsIn.join(',') : undefined,
      with_attachments: with_attachments
        ? Boolean(with_attachments)
        : undefined,
      seller_public_id: seller_public_id ? Number(seller_public_id) : undefined,
      locale: localeParam ? String(localeParam) : undefined,
      translation_locale:
        (translation_locale ?? locale)
          ? String(translation_locale || locale)
          : undefined,
      cursor:
        cursorOverride || cursor ? String(cursorOverride || cursor) : undefined,
      ...(pageSize !== undefined && { page_size: pageSize }),
      order_by:
        (order_by ?? defaultOrderBy)
          ? String(order_by ?? defaultOrderBy)
          : undefined,
    })
    /* eslint-enable camelcase */
  })
}
